// SecureBookReader.js
// Protected book reader. Pages are drawn onto <canvas> with PDF.js, so the browser's own
// PDF viewer never loads: no Save as, Print, Add text, or download button.
// The PDF bytes come from the `book-reader` Edge Function, never from a public storage URL.
// Preview mode (books.allow_full_read = false): the server sends only the first N pages.
import { useEffect, useRef, useState } from "react";
import { supabase } from "../supabaseClient";
import "../styles/SecureBookReader.css";

// Pinned PDF.js build (UMD) + CMaps/fonts needed for Arabic PDFs with CID fonts.
const PDFJS = "https://cdn.jsdelivr.net/npm/pdfjs-dist@3.11.174";
let pdfjsPromise = null;

// Loads PDF.js once per page visit and reuses it.
function loadPdfJs() {
  if (window.pdfjsLib) return Promise.resolve(window.pdfjsLib);
  if (pdfjsPromise) return pdfjsPromise;
  pdfjsPromise = new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = `${PDFJS}/build/pdf.min.js`;
    s.async = true;
    s.onload = () => {
      window.pdfjsLib.GlobalWorkerOptions.workerSrc = `${PDFJS}/build/pdf.worker.min.js`;
      resolve(window.pdfjsLib);
    };
    s.onerror = () => { pdfjsPromise = null; reject(new Error("pdfjs_load")); };
    document.head.appendChild(s);
  });
  return pdfjsPromise;
}

// Faint diagonal site watermark drawn into the page pixels (discourages screenshot sharing).
function stamp(ctx, w, h) {
  ctx.save();
  ctx.globalAlpha = 0.07;
  ctx.fillStyle = "#000";
  ctx.font = `${Math.round(w / 22)}px sans-serif`;
  ctx.textAlign = "center";
  ctx.translate(w / 2, h / 2);
  ctx.rotate(-Math.PI / 6);
  for (let y = -h; y <= h; y += w / 4) ctx.fillText("wajdiabdulsamad.com", 0, y);
  ctx.restore();
}

// One page: renders only when it scrolls near the viewport (keeps long parts light on phones).
function PageCanvas({ doc, pageNum, width }) {
  const holderRef = useRef(null);
  const canvasRef = useRef(null);
  const [visible, setVisible] = useState(false);
  const [ratio, setRatio] = useState(1.414);

  useEffect(() => {
    const el = holderRef.current;
    if (!el) return undefined;
    const io = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVisible(true); io.disconnect(); } },
      { rootMargin: "800px 0px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    if (!visible || !doc || !width) return undefined;
    let task = null;
    let cancelled = false;
    (async () => {
      const page = await doc.getPage(pageNum);
      if (cancelled) return;
      const base = page.getViewport({ scale: 1 });
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const vp = page.getViewport({ scale: (width / base.width) * dpr });
      setRatio(base.height / base.width);
      const c = canvasRef.current;
      if (!c) return;
      c.width = Math.floor(vp.width);
      c.height = Math.floor(vp.height);
      const ctx = c.getContext("2d");
      task = page.render({ canvasContext: ctx, viewport: vp });
      await task.promise;
      if (!cancelled) stamp(ctx, c.width, c.height);
    })().catch(() => {});
    return () => { cancelled = true; if (task) task.cancel(); };
  }, [visible, doc, pageNum, width]);

  return (
    <div ref={holderRef} className="sbr-page" style={{ aspectRatio: `1 / ${ratio}` }}>
      <canvas ref={canvasRef} className="sbr-canvas" draggable={false} />
    </div>
  );
}

// `book` uses the camelCase shape built in Books.js: id, title, pdfParts, pdfUrl, buyLink, allowFullRead, previewPages.
export default function SecureBookReader({ book, initialPart = 0, onClose, onOrder }) {
  const parts = Array.isArray(book.pdfParts) && book.pdfParts.length
    ? book.pdfParts
    : (book.pdfUrl ? [{ name: "جزء 1", url: book.pdfUrl }] : []);
  const full = book.allowFullRead === true;

  const [part, setPart] = useState(full ? initialPart : 0);
  const [doc, setDoc] = useState(null);
  const [numPages, setNumPages] = useState(0);
  const [status, setStatus] = useState("loading"); // loading | ready | error
  const [width, setWidth] = useState(0);
  const bodyRef = useRef(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  // Page width follows the reader width (max 760px for comfortable reading).
  useEffect(() => {
    const el = bodyRef.current;
    if (!el) return undefined;
    const ro = new ResizeObserver(([e]) => {
      setWidth(Math.min(Math.floor(e.contentRect.width) - 24, 760));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Fetch the (preview or full) bytes from the Edge Function and open them in PDF.js.
  useEffect(() => {
    let alive = true;
    let loaded = null;
    setStatus("loading"); setDoc(null); setNumPages(0);
    (async () => {
      try {
        const pdfjs = await loadPdfJs();
        const { data, error } = await supabase.functions.invoke("book-reader", {
          body: { bookId: book.id, part },
        });
        if (error || !data || typeof data.arrayBuffer !== "function") throw error || new Error("empty");
        const buf = await data.arrayBuffer();
        loaded = await pdfjs.getDocument({
          data: new Uint8Array(buf),
          cMapUrl: `${PDFJS}/cmaps/`,
          cMapPacked: true,
          standardFontDataUrl: `${PDFJS}/standard_fonts/`,
          isEvalSupported: false, // mitigates CVE-2024-4367 on PDF.js 3.x
        }).promise;
        if (!alive) { loaded.destroy(); return; }
        setDoc(loaded); setNumPages(loaded.numPages); setStatus("ready");
        if (bodyRef.current) bodyRef.current.scrollTop = 0;
      } catch (_e) {
        if (alive) setStatus("error");
      }
    })();
    return () => { alive = false; if (loaded) loaded.destroy(); };
  }, [book.id, part]);

  // Block the common save/print/copy shortcuts, lock page scroll, and flag <body> for print CSS.
  useEffect(() => {
    document.body.style.overflow = "hidden";
    document.body.classList.add("sbr-open");
    const onKey = (e) => {
      if (e.key === "Escape") { closeRef.current && closeRef.current(); return; }
      const k = (e.key || "").toLowerCase();
      if ((e.ctrlKey || e.metaKey) && ["s", "p", "c", "a"].includes(k)) {
        e.preventDefault(); e.stopPropagation();
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => {
      window.removeEventListener("keydown", onKey, true);
      document.body.classList.remove("sbr-open");
      // Keep the page locked only if the book details popup is still open; otherwise unlock scrolling.
      document.body.style.overflow = document.querySelector(".bk-overlay") ? "hidden" : "";
    };
  }, []);

  const block = (e) => e.preventDefault();
  const order = () => {
    if (onOrder) onOrder(book);
    else if (book.buyLink) window.open(book.buyLink, "_blank", "noopener,noreferrer");
  };
  const badge = full ? (parts[part]?.name || `جزء ${part + 1}`) : `معاينة · ${numPages || book.previewPages || ""} صفحات`;

  return (
    <div className="sbr-overlay" onClick={onClose}>
      <div
        className="sbr-modal"
        dir="rtl"
        role="dialog"
        aria-modal="true"
        aria-label={book.title}
        onClick={(e) => e.stopPropagation()}
        onContextMenu={block}
        onDragStart={block}
        onCopy={block}
        onCut={block}
      >
        <header className="sbr-head">
          <div className="sbr-title">
            <h3>{book.title}</h3>
            <span className="sbr-badge">{badge}</span>
          </div>
          {full && parts.length > 1 && (
            <nav className="sbr-parts" aria-label="أجزاء الديوان">
              {parts.map((p, i) => (
                <button
                  key={i}
                  type="button"
                  className={i === part ? "is-active" : ""}
                  onClick={() => setPart(i)}
                >
                  {p.name || `جزء ${i + 1}`}
                </button>
              ))}
            </nav>
          )}
          <button type="button" className="sbr-close" onClick={onClose}>إغلاق ✕</button>
        </header>

        <div className="sbr-body" ref={bodyRef}>
          {status === "loading" && <p className="sbr-state">جارٍ تحميل الصفحات…</p>}
          {status === "error" && (
            <p className="sbr-state sbr-error">تعذّر عرض الديوان حالياً. حاول مرة أخرى لاحقاً.</p>
          )}
          {status === "ready" && doc && Array.from({ length: numPages }, (_, i) => (
            <PageCanvas key={`${part}-${i + 1}`} doc={doc} pageNum={i + 1} width={width} />
          ))}
          {status === "ready" && !full && (
            <section className="sbr-end">
              <span className="sbr-end-label">{"// انتهت المعاينة"}</span>
              <p>هذه صفحات مختارة من الديوان. لقراءته كاملاً اطلب نسختك.</p>
              {(book.buyLink || onOrder) && (
                <button type="button" className="sbr-order" onClick={order}>اطلب ديوانك</button>
              )}
            </section>
          )}
        </div>

        <footer className="sbr-foot">
          <span>الشاعر وجدي عبد الصمد – جميع الحقوق محفوظة</span>
          {(book.buyLink || onOrder) && (
            <button type="button" className="sbr-order" onClick={order}>اطلب ديوانك</button>
          )}
        </footer>
      </div>
    </div>
  );
}
